import {DependencyRegistrer, DocumentModel, ModelEventHandler} from '@/engine';
import {ArrayRowsAction, Event} from '@/model/types';
import {getAction} from '../actions';
import {recursiveReplaceIndex} from '../utils/object';
import {createConditionFactory} from './condition';

export const createEventHandler = (event: Event, dependencyRegistrer?: DependencyRegistrer): ModelEventHandler => {
  if ('type' in event.action && event.action.type === 'arraySet') {
    return createArrayRowsEventHandler(event, dependencyRegistrer);
  }

  const actionFactory = getAction(event.action);
  const conditions = (event.conditions || []).map(condition => createConditionFactory(condition, dependencyRegistrer));
  return (store, valueResolver, visibilitySet, documentModel?: DocumentModel) => {
    const handler = actionFactory(store, valueResolver, visibilitySet, documentModel, undefined, dependencyRegistrer);

    return (...args: unknown[]) => {
      const shouldExecute = conditions.every(condition => condition(valueResolver, visibilitySet)());
      if (shouldExecute) {
        return handler(...args);
      }
      return true;
    };
  };
};

export const createArrayRowsEventHandler = (
  event: Event,
  dependencyRegistrer?: DependencyRegistrer,
): ModelEventHandler => {
  const action = event.action as ArrayRowsAction;
  const repeat = {$ref: `_${action.array}[count]`};

  return (store, valueResolver, visibilitySet, documentModel?: DocumentModel) => {
    const repeatCount = Number(valueResolver(repeat));

    const conditions = (event.conditions || []).map(condition =>
      createConditionFactory(condition, dependencyRegistrer),
    );
    const handlers = Array.from({length: repeatCount}).map((_, index) => {
      const mappedAction = recursiveReplaceIndex(action, index);

      const actionFactory = getAction(mappedAction);
      const handler = actionFactory(store, valueResolver, visibilitySet, documentModel, undefined, dependencyRegistrer);

      return (...args: unknown[]) => handler(...args);
    });

    return (...args: unknown[]) => {
      const shouldExecute = conditions.every(condition => condition(valueResolver, visibilitySet)());

      if (shouldExecute) {
        return handlers.reduce((acc, handler) => acc.then(() => handler(...args)), Promise.resolve(true));
      }

      return true;
    };
  };
};
